import { apiClient } from '@/services/Axios';
import {
  toLegacyPaginated,
  unwrapV1,
  v1Delete,
  v1Get,
  v1Post,
  v1Put,
} from '@/services/v1Api';

/**
 * Get freelance staff list. Optionally filter by search, letter, bookable, vat_registered, sort.
 * Fetches all pages and returns the combined data array.
 * GET /api/v1/freelance-staff
 * GET /api/v1/freelance-staff?search=smith&letter=A&bookable=1&sort_by=name&sort_dir=asc
 */
export const getFreelanceStaff = async (searchOrParams = '') => {
    const params = typeof searchOrParams === 'string' ? { search: searchOrParams } : (searchOrParams || {});
    const query = { page: 1 };
    if (params.search && String(params.search).trim()) query.search = String(params.search).trim();
    if (params.letter) query.letter = params.letter;
    if (params.bookable != null) query.bookable = params.bookable ? '1' : '0';
    if (params.vat_registered != null) query.vat_registered = params.vat_registered ? '1' : '0';
    if (params.sort_by) query.sort_by = params.sort_by;
    if (params.sort_dir) query.sort_dir = params.sort_dir;
    if (params.exclude_assigned_to_project != null && params.exclude_assigned_to_project !== '') {
        query.exclude_assigned_to_project = params.exclude_assigned_to_project;
    }
    if (params.per_page != null) query.per_page = params.per_page;

    const firstPage = await v1Get('/api/v1/freelance-staff', query);
    const paginated = toLegacyPaginated(
        Array.isArray(firstPage.data) ? firstPage.data : [],
        firstPage.meta,
    );
    const firstPageData = paginated.data;
    const lastPage = paginated.last_page ?? 1;
    if (lastPage <= 1) {
        return firstPageData;
    }
    const all = [...firstPageData];
    for (let page = 2; page <= lastPage; page++) {
        const nextPage = await v1Get('/api/v1/freelance-staff', { ...query, page });
        const nextPaginated = toLegacyPaginated(
            Array.isArray(nextPage.data) ? nextPage.data : [],
            nextPage.meta,
        );
        all.push(...nextPaginated.data);
    }
    return all;
};

/**
 * Add freelancer to company by share code. Company user only.
 * POST /api/v1/freelance-staff/add-by-code
 * Body: { share_code: "XY7K2M9PQR" }
 * 201: freelancer added; 403: no company_id; 404: no freelancer; 409: already in company
 */
export const addFreelancerByCode = async (shareCode) => {
    const { data } = await v1Post('/api/v1/freelance-staff/add-by-code', {
        share_code: String(shareCode).trim(),
    });
    return data;
};

/**
 * Get the current user's freelancer profile (freelance users only).
 * GET /api/v1/freelance-staff/me
 */
export const getFreelanceStaffMe = async () => {
    const { data } = await v1Get('/api/v1/freelance-staff/me');
    return data;
};

/**
 * Get one freelancer by id or hashid.
 * GET /api/v1/freelance-staff/{id}
 */
export const getFreelanceStaffById = async (id) => {
    const { data } = await v1Get(`/api/v1/freelance-staff/${id}`);
    return data;
};

/**
 * Update freelancer. Same body shape as "Update my profile".
 * PUT or PATCH /api/v1/freelance-staff/{id}
 */
export const updateFreelanceStaff = async (id, payload) => {
    const { data } = await v1Put(`/api/v1/freelance-staff/${id}`, payload);
    return data;
};

/**
 * Upload freelancer profile image.
 * POST /api/v1/freelance-staff/{id}/upload-image
 * FormData: image (file)
 */
export const uploadFreelanceStaffImage = async (id, imageFile) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    const response = await apiClient.post(`/api/v1/freelance-staff/${id}/upload-image`, formData);
    return unwrapV1(response).data;
};

/**
 * Delete freelancer profile image.
 * DELETE /api/v1/freelance-staff/{id}/image
 */
export const deleteFreelanceStaffImage = async (id) => {
    const { data } = await v1Delete(`/api/v1/freelance-staff/${id}/image`);
    return data;
};

// ─── Master specialisms (tools/platforms) ─────────────────────────────────
export const getFreelanceStaffSpecialisms = async () => {
    const { data } = await v1Get('/api/v1/freelance-staff-specialisms');
    return Array.isArray(data) ? data : [];
};

export const createFreelanceStaffSpecialism = async (payload) => {
    const { data } = await v1Post('/api/v1/freelance-staff-specialisms', payload);
    return data;
};

export const updateFreelanceStaffSpecialism = async (id, payload) => {
    const { data } = await v1Put(`/api/v1/freelance-staff-specialisms/${id}`, payload);
    return data;
};

export const deleteFreelanceStaffSpecialism = async (id) => {
    await v1Delete(`/api/v1/freelance-staff-specialisms/${id}`);
};

/** Sync specialisms for a role: PUT /api/v1/freelance-staff-roles/{roleId}/specialisms body { specialism_master_ids: [1,2,3] } */
export const syncRoleSpecialisms = async (roleId, specialismMasterIds) => {
    const { data } = await v1Put(`/api/v1/freelance-staff-roles/${roleId}/specialisms`, {
        specialism_master_ids: specialismMasterIds ?? [],
    });
    return data;
};

// ─── Per-freelancer specialisms ───────────────────────────────────────────
/** GET /api/v1/freelance-staff/{id}/specialisms */
export const getFreelancerSpecialisms = async (freelanceStaffId) => {
    const { data } = await v1Get(`/api/v1/freelance-staff/${freelanceStaffId}/specialisms`);
    return Array.isArray(data) ? data : [];
};

export const addFreelancerSpecialism = async (freelanceStaffId, payload) => {
    const { data } = await v1Post(`/api/v1/freelance-staff/${freelanceStaffId}/specialisms`, payload);
    return data;
};

export const updateFreelancerSpecialism = async (freelanceStaffId, specialismId, payload) => {
    const { data } = await v1Put(
        `/api/v1/freelance-staff/${freelanceStaffId}/specialisms/${specialismId}`,
        payload,
    );
    return data;
};

export const deleteFreelancerSpecialism = async (freelanceStaffId, specialismId) => {
    await v1Delete(`/api/v1/freelance-staff/${freelanceStaffId}/specialisms/${specialismId}`);
};

// ─── Master certifications ───────────────────────────────────────────────
export const getFreelanceStaffCertifications = async () => {
    const { data } = await v1Get('/api/v1/freelance-staff-certifications');
    return Array.isArray(data) ? data : [];
};

export const createFreelanceStaffCertification = async (payload) => {
    const { data } = await v1Post('/api/v1/freelance-staff-certifications', payload);
    return data;
};

export const updateFreelanceStaffCertification = async (id, payload) => {
    const { data } = await v1Put(`/api/v1/freelance-staff-certifications/${id}`, payload);
    return data;
};

export const deleteFreelanceStaffCertification = async (id) => {
    await v1Delete(`/api/v1/freelance-staff-certifications/${id}`);
};

// ─── Per-freelancer certifications (expiry_date, document_id for evidence) ─
/** GET /api/v1/freelance-staff/{id}/certifications */
export const getFreelancerCertifications = async (freelanceStaffId) => {
    const { data } = await v1Get(`/api/v1/freelance-staff/${freelanceStaffId}/certifications`);
    return Array.isArray(data) ? data : [];
};

export const addFreelancerCertification = async (freelanceStaffId, payload) => {
    const { data } = await v1Post(`/api/v1/freelance-staff/${freelanceStaffId}/certifications`, payload);
    return data;
};

export const updateFreelancerCertification = async (freelanceStaffId, certificationId, payload) => {
    const { data } = await v1Put(
        `/api/v1/freelance-staff/${freelanceStaffId}/certifications/${certificationId}`,
        payload,
    );
    return data;
};

export const deleteFreelancerCertification = async (freelanceStaffId, certificationId) => {
    await v1Delete(`/api/v1/freelance-staff/${freelanceStaffId}/certifications/${certificationId}`);
};

// ─── Company ratings (company users only; dimensions: technical, timekeeping, communication, reliability) ─
export const getFreelancerCompanyRatings = async (freelanceStaffId) => {
    const { data } = await v1Get(`/api/v1/freelance-staff/${freelanceStaffId}/company-ratings`);
    return Array.isArray(data) ? data : [];
};

/** Set/update: body { dimension, rating, notes }. Dimensions: technical, timekeeping, communication, reliability. Rating 1–5. */
export const setFreelancerCompanyRating = async (freelanceStaffId, payload) => {
    const { data } = await v1Post(`/api/v1/freelance-staff/${freelanceStaffId}/company-ratings`, payload);
    return data;
};

export const updateFreelancerCompanyRating = async (freelanceStaffId, ratingId, payload) => {
    const { data } = await v1Put(
        `/api/v1/freelance-staff/${freelanceStaffId}/company-ratings/${ratingId}`,
        payload,
    );
    return data;
};

export const deleteFreelancerCompanyRating = async (freelanceStaffId, ratingId) => {
    await v1Delete(`/api/v1/freelance-staff/${freelanceStaffId}/company-ratings/${ratingId}`);
};
